import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import moment from 'moment';

import { customersListActions, customersListSelectors } from 'common/config/store';
import Layout from 'components/templates/layout';
import CustomersFilter from 'components/templates/customers-filter';
import { useIntl } from 'common/config/i18n';
import { StyledMainTitle, StyledListUtil, StyledBlueLink } from 'common/styling/mixins';
import { FluxClientsFilters } from 'types';
import history from 'app-router/history';
import CustomersByStationList from 'components/templates/customers-by-station-list';
import useFromMenu from 'hooks/use-from-menu';

const { StyledListWrapper, StyledListTitleWrapper } = StyledListUtil;

const Customers: React.FC = () => {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const isFromMenu = useFromMenu({ clearAfterRead: true });

  const filterValues = useSelector(customersListSelectors.filtersSelector);
  const status = useSelector(customersListSelectors.customersByStationStatusSelector);
  const data = useSelector(customersListSelectors.customersByStationDataSelector);

  useEffect(() => {
    // We wait untill isFromMenu has a value (false or true)
    if (isFromMenu !== null) {
      if (isFromMenu) {
        dispatch(customersListActions.resetFilters());
        dispatch(
          customersListActions.fetchCustomersByStation({
            startDate: moment().format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            pageNumber: 1,
            maxResultPage: 6,
            includedCancel: false,
            includedRolling: false,
          })
        );
      } else {
        dispatch(
          customersListActions.setFilters({
            ...filterValues,
            station: undefined,
            arrivalStation: undefined,
            departureStation: undefined,
          })
        );
        dispatch(
          customersListActions.fetchCustomersByStation({
            ...filterValues,
            startDate: moment(filterValues.startDate).format('YYYY-MM-DD'),
            endDate: moment(filterValues.endDate).format('YYYY-MM-DD'),
            station: undefined,
            arrivalStation: undefined,
            departureStation: undefined,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFromMenu]);

  const launchAction = (values: FluxClientsFilters, action: (params: FluxClientsFilters) => void) => {
    const params = {
      ...values,
      startDate: moment(values.startDate).format('YYYY-MM-DD'),
      endDate: moment(values.endDate).format('YYYY-MM-DD'),
      pageNumber: 1,
      maxResultPage: 6,
    };
    dispatch(customersListActions.setFilters(params));
    dispatch(action(params));
  };

  const fetchAction = (values: FluxClientsFilters) => {
    launchAction(values, customersListActions.fetchCustomersByStation);
  };
  const refreshAction = (values: FluxClientsFilters) => {
    launchAction(values, customersListActions.refreshCustomersByStation);
  };
  const exportListAction = (values: FluxClientsFilters) => {
    launchAction(values, customersListActions.exportCustomersByStation);
  };

  return (
    <div>
      <Layout />
      <StyledListWrapper>
        <StyledMainTitle withMarginBottom>{t('customersStationPage', 'title')}</StyledMainTitle>
        <StyledListTitleWrapper>
          <StyledBlueLink
            noDecoration
            onClick={() => {
              history.replace('/customers/OD');
            }}
          >
            {t('customersStationPage', 'linkOD')}
          </StyledBlueLink>
        </StyledListTitleWrapper>
        <CustomersFilter
          mode="customersByStation"
          status={status}
          fetchAction={fetchAction}
          refreshAction={refreshAction}
          exportListAction={exportListAction}
          searchDate={data?.searchTimestamp}
        />
        {status === 'success' && <CustomersByStationList data={data} filterValues={filterValues} />}
        {status === 'loading' && <Skeleton />}
      </StyledListWrapper>
    </div>
  );
};

export default Customers;
